import React, { useState, useMemo, useCallback } from "react"
import { Modal, Input, Checkbox } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { SearchOutlined } from "@ant-design/icons"
import { useExportMapStore } from "stores/exportMapStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

interface ExportMapMaxMultiSetModalProps {
  open: boolean
  onClose: () => void
}

interface MatchResult {
  variableLabel: string
  variableName: string
  currentCode: number
}

const ExportMapMaxMultiSetModal: React.FC<ExportMapMaxMultiSetModalProps> = ({
  open,
  onClose,
}) => {
  const { allVariables, bulkSetMaxMultiCode } = useExportMapStore()

  const [searchPattern, setSearchPattern] = useState("")
  const [newCode, setNewCode] = useState("")
  const [caseInsensitive, setCaseInsensitive] = useState(true)
  const [exactMatch, setExactMatch] = useState(false)

  const matchingVariables = useMemo((): MatchResult[] => {
    if (!searchPattern.trim()) return []

    const results: MatchResult[] = []
    const pattern = caseInsensitive ? searchPattern.toLowerCase() : searchPattern

    for (const variable of allVariables) {
      if (variable.type !== "BOOL" || variable.maxMultiCode === undefined) continue

      const label = caseInsensitive
        ? variable.fullLabel.toLowerCase()
        : variable.fullLabel

      const matches = exactMatch ? label === pattern : label.includes(pattern)

      if (matches) {
        results.push({
          variableLabel: variable.fullLabel,
          variableName:
            variable.path[variable.path.length - 1] || variable.fullLabel,
          currentCode: variable.maxMultiCode,
        })
      }
    }

    return results
  }, [searchPattern, caseInsensitive, exactMatch, allVariables])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchPattern(e.target.value)
    },
    []
  )

  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewCode(e.target.value)
    },
    []
  )

  const trimmedCode = newCode.trim()
  const isValidCode = /^\d+$/.test(trimmedCode)
  const showCodeError = trimmedCode.length > 0 && !isValidCode

  const handleApply = () => {
    if (!searchPattern.trim()) {
      notify.toast.warning("Please enter a search pattern")
      return
    }

    if (!isValidCode) {
      notify.toast.warning("Please enter a positive integer code")
      return
    }

    const parsedCode = parseInt(trimmedCode, 10)

    const { variableCount } = bulkSetMaxMultiCode(
      searchPattern,
      parsedCode,
      caseInsensitive,
      exactMatch
    )

    if (variableCount > 0) {
      notify.toast.success(`Updated max-multi code on ${variableCount} variables`)
    } else {
      notify.toast.info("No matching max-multi variables found")
    }

    setSearchPattern("")
    setNewCode("")
    onClose()
  }

  const handleClose = () => {
    setSearchPattern("")
    setNewCode("")
    setCaseInsensitive(true)
    setExactMatch(false)
    onClose()
  }

  const isApplyDisabled =
    !searchPattern.trim() ||
    !isValidCode ||
    matchingVariables.length === 0

  return (
    <Modal
      title="Set Max-Multi Codes"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={550}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Find max-multi fields whose label path contains
          </label>
          <Input
            placeholder="e.g., Brands//Likes//Coke or just Coke"
            value={searchPattern}
            onChange={handleSearchChange}
            prefix={<SearchOutlined className="text-gray-400" />}
            allowClear
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Set max-multi code to
          </label>
          <Input
            placeholder="e.g., 99"
            value={newCode}
            onChange={handleCodeChange}
            maxLength={10}
            className="w-40"
            status={showCodeError ? "error" : ""}
          />
          {showCodeError && (
            <div className="text-xs text-red-500 mt-1">
              Max-multi codes must be positive integers
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <Checkbox
            checked={caseInsensitive}
            onChange={(e) => setCaseInsensitive(e.target.checked)}
          >
            Case insensitive
          </Checkbox>
          <Checkbox
            checked={exactMatch}
            onChange={(e) => setExactMatch(e.target.checked)}
          >
            Exact match only
          </Checkbox>
        </div>

        {searchPattern.trim() && (
          <div className="bg-gray-50 p-3 rounded">
            {matchingVariables.length > 0 ? (
              <>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Found {matchingVariables.length} matching{" "}
                  {matchingVariables.length === 1 ? "variable" : "variables"}
                </div>
                <div className="max-h-40 overflow-auto space-y-1">
                  {matchingVariables.slice(0, 10).map((match, i) => (
                    <div
                      key={`${match.variableLabel}-${i}`}
                      className="text-xs flex items-center gap-2"
                    >
                      <span className="text-gray-500 truncate max-w-[260px]">
                        {match.variableLabel}
                      </span>
                      <span className="text-gray-400">
                        (currently: {match.currentCode})
                      </span>
                    </div>
                  ))}
                  {matchingVariables.length > 10 && (
                    <div className="text-xs text-gray-400 mt-1">
                      ...and {matchingVariables.length - 10} more
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500">
                No matching max-multi variables found
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-gray-500">
          {exactMatch
            ? "Matches BOOL fields (with max-multi enabled) whose full label path exactly equals the search text."
            : "Matches BOOL fields (with max-multi enabled) whose full label path contains the search text."}
          {" "}Useful for re-numbering the max-multi values across many fields at once.
        </div>

        <div className="flex flex-row space-x-2 pt-2">
          <DataQButton className="ml-auto" type="primary" onClick={handleClose}>
            Cancel
          </DataQButton>
          <DataQButton
            type="secondary"
            onClick={handleApply}
            isDisabled={isApplyDisabled}
          >
            Apply
          </DataQButton>
        </div>
      </div>
    </Modal>
  )
}

export default ExportMapMaxMultiSetModal
